import {createSyncQueue} from '../offline/queue.js';
import {createSmokingSessionStore,SMOKING_SESSION_ENTITY_TYPE} from './session-store.js';
import {createSmokingSessionEditor} from './session-editor.js';
import {createSmokingItemStore} from './item-store.js';
import {createSmokingItemEditor} from './item-editor.js';
import {createItemPreparationStore} from './preparation-store.js';
import {createSmokingStageStore} from './stage-store.js';
import {createStageTargetStore} from './stage-target-store.js';
import {createSmokingStageEditor} from './stage-editor.js';
import {createSessionWoodStore} from './wood-mix-store.js';
import {createSessionWoodEditor} from './wood-mix-editor.js';
import {createJournalNoteStore} from './journal-note-store.js';
import {createCustomProductStore} from './custom-product-store.js';
import {createProductCatalogueService} from './product-catalogue-service.js';
import {createJournalSyncProcessor} from './sync.js';
import {createJournalOfflineInspector} from './offline.js';
import {createJournalPhotoStore} from './journal-photo-store.js';
import {createJournalPhotoFlow} from './journal-photo-flow.js';
import {createTemperatureReadingStore} from '../live/temperature-reading-store.js';
import {createLiveEventStore} from '../live/live-event-store.js';
import {createLiveQuickActions} from '../live/quick-actions.js';
import {createLiveStageTransitionStore} from '../live/stage-transition-store.js';
import {createLiveStageTransitionService} from '../live/stage-transition-service.js';
import {createLiveNoteStore} from '../live/live-note-store.js';
import {createLiveNotificationPreferenceStore} from '../live/notification-preferences.js';
import {createLiveNotificationScheduler} from '../live/notification-scheduler.js';
import {createLiveSessionRecovery} from '../live/session-recovery.js';
import {createLiveSessionConflictResolver} from './live-conflict-resolution.js';
import {createLiveAnalytics} from '../live/analytics.js';
import {createLiveObservability} from '../live/observability.js';
import {createSessionOutcomeStore} from '../history/session-outcome.js';
import {createRecipeAnalytics} from '../recipes/analytics.js';
import {createPlannerAnalytics,plannerRunIdFromSession} from '../planner/analytics.js';

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

export function createJournalRuntime({
  repository,
  userId,
  remoteAdapters=null,
  productCatalogueRemote=null,
  photoStorage=null,
  photoRemote=null,
  smokehouse=null,
  diagnostics=null,
  analytics=null,
  observabilityContext={},
  observabilitySink=()=>{},
  notificationAdapter=null,
  now=()=>new Date().toISOString(),
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!repository?.get||!repository?.put||!repository?.list||!repository?.listPending){
    throw new TypeError('Journal repository is required');
  }
  const owner=required(userId,'userId');
  const liveAnalytics=createLiveAnalytics({analytics});
  const recipeAnalytics=createRecipeAnalytics({analytics});
  const plannerAnalytics=createPlannerAnalytics({analytics});
  const liveObservability=createLiveObservability({
    appVersion:observabilityContext?.appVersion||'unknown',
    platform:observabilityContext?.platform||'web',
    environment:observabilityContext?.environment||'dev',
    diagnostics,
    sink:observabilitySink,
    now
  });
  const queue=createSyncQueue({repository,now});
  const sessions=createSmokingSessionStore({
    repository,queue,userId:owner,now,
    onLifecycle({previous,next}){
      const offline=!isOnline();
      if(previous?.status!=='smoking'&&next?.status==='smoking'){
        liveAnalytics.sessionStarted({offline});
        liveAnalytics.stageStarted({reason:'initial',offline});
        if(next?.recipeId)recipeAnalytics.sessionStarted({session:next,offline});
        const plannerRunId=plannerRunIdFromSession(next);
        if(plannerRunId)plannerAnalytics.sessionStarted({plannerRunId,offline});
      }
      if(previous?.status==='smoking'&&['resting','completed'].includes(next?.status)){
        liveAnalytics.stageCompleted({reason:'terminal',offline});
      }
      if(previous?.status!=='completed'&&next?.status==='completed'&&next?.actualStartAt){
        liveAnalytics.sessionCompleted({offline});
        if(next?.recipeId)recipeAnalytics.sessionCompleted({session:next,offline});
        const plannerRunId=plannerRunIdFromSession(next);
        if(plannerRunId)plannerAnalytics.sessionCompleted({plannerRunId,offline});
      }
      if(previous?.status!=='cancelled'&&next?.status==='cancelled'&&next?.actualStartAt){
        liveAnalytics.sessionCancelled({offline});
      }
    }
  });
  const items=createSmokingItemStore({repository,queue,userId:owner,now});
  const preparations=createItemPreparationStore({repository,queue,userId:owner,now});
  const stages=createSmokingStageStore({repository,queue,userId:owner,now});
  const targets=createStageTargetStore({repository,queue,userId:owner,now});
  const woods=createSessionWoodStore({repository,queue,userId:owner,now});
  const notes=createJournalNoteStore({repository,queue,userId:owner,now});
  const customProducts=createCustomProductStore({repository,queue,userId:owner,now});
  const temperatures=createTemperatureReadingStore({repository,queue,userId:owner,now});
  const events=createLiveEventStore({repository,queue,userId:owner,now});
  const stageTransitions=createLiveStageTransitionStore({repository,queue,userId:owner,now});
  const liveNotes=createLiveNoteStore({repository,queue,userId:owner,now});
  const outcomes=createSessionOutcomeStore({repository,queue,userId:owner,now});
  const liveNotificationPreferences=createLiveNotificationPreferenceStore({
    repository,userId:owner,now
  });
  const liveNotificationAdapter=notificationAdapter;
  const liveNotifications=createLiveNotificationScheduler({
    adapter:liveNotificationAdapter,
    preferenceStore:liveNotificationPreferences,
    stageTargets:targets,
    now
  });
  let liveNotificationInteractionHandle=null;
  const liveNotificationInteractionReady=Promise.resolve(
    liveNotifications.onInteraction(({kind,action})=>{
      liveAnalytics.notificationInteraction({kind,action});
      liveObservability.recordNotificationInteraction({kind,action});
    })
  ).then(handle=>{
    liveNotificationInteractionHandle=handle||null;
    return handle||null;
  }).catch(error=>{
    try{diagnostics?.capture?.('journal.live-notification-listener',error);}catch{}
    return null;
  });
  const liveRecovery=createLiveSessionRecovery({
    sessions,
    notificationReconciler:liveNotifications,
    diagnostics
  });
  const productCatalogue=createProductCatalogueService({
    repository,
    remote:productCatalogueRemote,
    diagnostics,
    isOnline
  });
  const offline=createJournalOfflineInspector({repository,queue,userId:owner});

  const remote=remoteAdapters;
  const sync=remote?createJournalSyncProcessor({repository,queue,remoteAdapters:remote}):null;
  const liveConflicts=remote?.[SMOKING_SESSION_ENTITY_TYPE]?.fetch
    ?createLiveSessionConflictResolver({
      repository,
      sessions,
      remoteSession:remote[SMOKING_SESSION_ENTITY_TYPE],
      userId:owner,
      now
    })
    :null;

  const photos=createJournalPhotoStore({repository,userId:owner,now});
  const photoFlow=createJournalPhotoFlow({
    store:photos,
    storage:photoStorage,
    remote:photoRemote,
    isOnline
  });

  async function pendingCount(){
    const summary=await offline.pendingSummary();
    return Object.values(summary||{}).reduce((sum,value)=>sum+Number(value||0),0);
  }

  async function syncPending(){
    const flowId=diagnostics?.createFlowId?.()||null;
    const recordSyncEvent=(method,input)=>liveObservability[method]({...input,flowId});
    let queueBefore=0;
    try{queueBefore=await pendingCount();}catch{}
    if(!isOnline()){
      recordSyncEvent('recordSync',{
        status:'offline',
        queueBefore,
        queueAfter:queueBefore
      });
      return Object.freeze({ok:false,status:'offline'});
    }

    try{
      const photoBefore=await photoFlow.retryPending();
      if(!sync){
        recordSyncEvent('recordSync',{
          status:'unavailable',
          queueBefore,
          queueAfter:queueBefore,
          errorCode:'REMOTE_UNAVAILABLE'
        });
        return Object.freeze({
          ok:false,
          status:'remote_unavailable',
          photoResult:photoBefore
        });
      }

      const result=await sync.processPending();
      // A binary may upload before its fully-offline session exists remotely.
      // Retry metadata once dependencies are synchronized so one reconnect run
      // can finish the whole Live capture whenever possible.
      const photoAfter=await photoFlow.retryPending();
      let queueAfter=queueBefore;
      try{queueAfter=await pendingCount();}catch{}
      const partial=Number(result?.retries||0)>0||Number(result?.conflicts||0)>0;
      recordSyncEvent('recordSync',{
        status:partial?'partial':'success',
        queueBefore,
        queueAfter,
        processed:result?.processed,
        retries:result?.retries,
        conflicts:result?.conflicts
      });
      if(queueBefore>queueAfter&&!partial){
        recordSyncEvent('recordSyncRecovery',{
          queueBefore,
          queueAfter,
          recoveredCount:queueBefore-queueAfter
        });
      }
      return Object.freeze({
        ok:true,
        status:'processed',
        photoResult:Object.freeze({before:photoBefore,after:photoAfter}),
        ...result
      });
    }catch(error){
      let queueAfter=queueBefore;
      try{queueAfter=await pendingCount();}catch{}
      const failureCode=String(error?.code||error?.name||'JOURNAL_SYNC_FAILED').slice(0,80);
      recordSyncEvent('recordSync',{
        status:'failure',
        queueBefore,
        queueAfter,
        errorCode:failureCode
      });
      try{diagnostics?.capture?.('journal.live-sync',error,{queue_depth:queueBefore,flowId});}catch{}
      throw error;
    }
  }

  async function referenceData({locale='pl'}={}){
    const productResult=await productCatalogue.load({locale,refresh:true});
    const custom=await customProducts.list();

    let smokers=[];
    let capabilities=[];
    let woodInventory=[];
    let woodCatalogue=[];
    if(smokehouse){
      try{
        [smokers,capabilities,woodInventory]=await Promise.all([
          smokehouse.smokers?.list?.({userId:owner})||[],
          smokehouse.capabilities?.list?.({userId:owner})||[],
          smokehouse.wood?.list?.()||[]
        ]);
        const result=await smokehouse.woodCatalogue?.load?.({locale,refresh:true});
        woodCatalogue=result?.items||[];
      }catch(error){
        try{diagnostics?.capture?.('journal.reference-data',error);}catch{}
      }
    }

    return Object.freeze({
      productCatalogue:productResult.items,
      productCatalogueSource:productResult.source,
      customProducts:custom,
      smokers:Object.freeze([...(smokers||[])]),
      capabilities:Object.freeze([...(capabilities||[])]),
      woodInventory:Object.freeze([...(woodInventory||[])]),
      woodCatalogue:Object.freeze([...(woodCatalogue||[])])
    });
  }

  async function completedHistoryBundles(){
    const completed=await sessions.list({status:'completed'});
    const result=[];
    for(const session of completed)result.push(await sessionBundle(session.id));
    return Object.freeze(result.filter(Boolean));
  }

  async function sessionBundle(id){
    const session=await sessions.get(required(id,'sessionId'));
    if(!session)return null;
    const itemRows=await items.list(session.id);
    const stageRows=await stages.list(session.id);
    const [woodRows,sessionNote,photoRows,temperatureRows,eventRows,transitionRows,liveNoteRows]=await Promise.all([
      woods.list(session.id),
      notes.getSession(session.id),
      photos.list({sessionId:session.id}),
      temperatures.list(session.id),
      events.list(session.id),
      stageTransitions.list(session.id),
      liveNotes.list(session.id)
    ]);
    const preparationRows=[];
    const itemNotes=[];
    for(const item of itemRows){
      preparationRows.push(await preparations.get(item.id));
      itemNotes.push(await notes.getItem(item.id));
    }
    const stageTargets=[];
    const itemTargets=[];
    for(const stage of stageRows){
      stageTargets.push(await targets.getStageTarget(stage.id));
      itemTargets.push(...await targets.listItemTargets(stage.id));
    }

    return Object.freeze({
      session,
      items:itemRows,
      preparations:Object.freeze(preparationRows.filter(Boolean)),
      itemNotes:Object.freeze(itemNotes.filter(Boolean)),
      woods:woodRows,
      stages:stageRows,
      stageTargets:Object.freeze(stageTargets.filter(Boolean)),
      itemTargets:Object.freeze(itemTargets),
      sessionNote,
      photos:photoRows,
      temperatureReadings:temperatureRows,
      liveEvents:eventRows,
      liveNotes:liveNoteRows,
      liveStageTransitions:transitionRows,
      outcome:await outcomes.get(session.id)
    });
  }

  function liveQuickActions(id){
    return createLiveQuickActions({eventStore:events,sessionId:required(id,'sessionId')});
  }

  function liveStageTransitions(id){
    return createLiveStageTransitionService({
      sessions,
      stages,
      transitions:stageTransitions,
      events,
      conflictGuard:liveConflicts,
      sessionId:required(id,'sessionId'),
      now
    });
  }

  async function dispose(){
    try{
      const handle=liveNotificationInteractionHandle||await liveNotificationInteractionReady;
      await handle?.remove?.();
    }catch(error){
      try{diagnostics?.capture?.('journal.live-notification-listener.remove',error);}catch{}
    }finally{
      liveNotificationInteractionHandle=null;
    }
  }

  async function sessionControllers(id){
    const session=await sessions.get(required(id,'sessionId'));
    if(!session)throw new Error('SMOKING_SESSION_NOT_FOUND');

    const sessionEditor=createSmokingSessionEditor({
      sessionStore:sessions,
      itemStore:items,
      stageStore:stages,
      isOnline
    });
    await sessionEditor.open(session.id);

    const itemEditor=createSmokingItemEditor({
      store:items,
      sessionId:session.id,
      sessionStatus:session.status,
      isOnline
    });
    const woodEditor=createSessionWoodEditor({
      store:woods,
      sessionId:session.id,
      sessionStatus:session.status,
      isOnline
    });
    const stageEditor=createSmokingStageEditor({
      stageStore:stages,
      targetStore:targets,
      sessionId:session.id,
      sessionStatus:session.status,
      items:await items.list(session.id),
      isOnline
    });
    await Promise.all([itemEditor.refresh(),woodEditor.refresh(),stageEditor.refresh()]);

    return Object.freeze({sessionEditor,itemEditor,woodEditor,stageEditor});
  }

  return Object.freeze({
    userId:owner,
    queue,
    sessions,
    items,
    preparations,
    stages,
    targets,
    woods,
    notes,
    customProducts,
    productCatalogue,
    photos,
    photoFlow,
    temperatures,
    events,
    stageTransitions,
    liveNotes,
    outcomes,
    liveNotificationPreferences,
    liveNotificationAdapter,
    liveNotifications,
    liveAnalytics,
    recipeAnalytics,
    liveObservability,
    liveRecovery,
    liveConflicts,
    liveQuickActions,
    liveStageTransitions,
    offline,
    sync,
    syncPending,
    referenceData,
    sessionBundle,
    completedHistoryBundles,
    sessionControllers,
    dispose
  });
}
